import { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import { Button } from '../common/Button';
import toast from 'react-hot-toast';
import transportService from '../../services/transportService';

type Forwarder = { name: string; email?: string; phone?: string };
type Driver = { name: string; email?: string; phone?: string };

interface CarrierDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  carrier: {
    id: number;
    name: string;
    email?: string;
    phone?: string;
    nip?: string;
    forwarders?: Forwarder[];
    drivers?: Driver[];
    is_active: boolean;
  } | null;
  onSuccess: () => void;
}

export const CarrierDetailsModal = ({ isOpen, onClose, carrier, onSuccess }: CarrierDetailsModalProps) => {
  const [carrierData, setCarrierData] = useState({
    name: carrier?.name || '',
    email: carrier?.email || '',
    phone: carrier?.phone || '',
    nip: carrier?.nip || '',
    is_active: carrier?.is_active ?? true
  });

  const [forwarders, setForwarders] = useState<Forwarder[]>(carrier?.forwarders || []);
  const [drivers, setDrivers] = useState<Driver[]>(carrier?.drivers || []);

  // Modal states for add/edit forwarder/driver
  const [forwarderModal, setForwarderModal] = useState<{ open: boolean; index: number | null }>({ open: false, index: null });
  const [driverModal, setDriverModal] = useState<{ open: boolean; index: number | null }>({ open: false, index: null });

  const [forwarderForm, setForwarderForm] = useState({ name: '', email: '', phone: '' });
  const [driverForm, setDriverForm] = useState({ name: '', email: '', phone: '' });

  // Synchronizuj state z props carrier gdy się zmienia
  useEffect(() => {
    if (carrier) {
      setCarrierData({
        name: carrier.name || '',
        email: carrier.email || '',
        phone: carrier.phone || '',
        nip: carrier.nip || '',
        is_active: carrier.is_active ?? true
      });
      setForwarders(carrier.forwarders || []);
      setDrivers(carrier.drivers || []);
    }
  }, [carrier]);

  if (!carrier) return null;

  const handleSaveCarrier = async () => {
    if (!carrierData.name.trim()) {
      toast.error('Nazwa przewoźnika jest wymagana');
      return;
    }

    try {
      await transportService.updateCarrier(carrier.id, {
        ...carrierData,
        forwarders,
        drivers
      });
      toast.success('Przewoźnik został zaktualizowany');
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error?.message || 'Nie udało się zaktualizować przewoźnika');
    }
  };

  const handleAddForwarder = () => {
    setForwarderForm({ name: '', email: '', phone: '' });
    setForwarderModal({ open: true, index: null });
  };

  const handleEditForwarder = (index: number) => {
    setForwarderForm(forwarders[index]);
    setForwarderModal({ open: true, index });
  };

  const handleSaveForwarder = () => {
    if (!forwarderForm.name.trim()) {
      toast.error('Imię i nazwisko spedytora jest wymagane');
      return;
    }

    if (forwarderModal.index !== null) {
      // Edit existing
      const updated = [...forwarders];
      updated[forwarderModal.index] = forwarderForm;
      setForwarders(updated);
    } else {
      // Add new
      setForwarders([...forwarders, forwarderForm]);
    }

    setForwarderModal({ open: false, index: null });
    toast.success('Spedytor został dodany');
  };

  const handleDeleteForwarder = (index: number) => {
    if (!confirm('Czy na pewno chcesz usunąć tego spedytora?')) return;
    setForwarders(forwarders.filter((_, i) => i !== index));
    toast.success('Spedytor został usunięty');
  };

  const handleAddDriver = () => {
    setDriverForm({ name: '', email: '', phone: '' });
    setDriverModal({ open: true, index: null });
  };

  const handleEditDriver = (index: number) => {
    setDriverForm(drivers[index]);
    setDriverModal({ open: true, index });
  };

  const handleSaveDriver = () => {
    if (!driverForm.name.trim()) {
      toast.error('Imię i nazwisko kierowcy jest wymagane');
      return;
    }

    if (driverModal.index !== null) {
      // Edit existing
      const updated = [...drivers];
      updated[driverModal.index] = driverForm;
      setDrivers(updated);
    } else {
      // Add new
      setDrivers([...drivers, driverForm]);
    }

    setDriverModal({ open: false, index: null });
    toast.success('Kierowca został dodany');
  };

  const handleDeleteDriver = (index: number) => {
    if (!confirm('Czy na pewno chcesz usunąć tego kierowcę?')) return;
    setDrivers(drivers.filter((_, i) => i !== index));
    toast.success('Kierowca został usunięty');
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={`Podgląd i edycja przewoźnika: ${carrier.name}`} size="xl">
        <div className="space-y-4">
          {/* Dane przewoźnika */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nazwa przewoźnika *</label>
              <input
                type="text"
                className="w-full border rounded px-3 py-2"
                value={carrierData.name}
                onChange={(e) => setCarrierData({ ...carrierData, name: e.target.value })}
                placeholder="Nazwa firmy"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">NIP</label>
              <input
                type="text"
                className="w-full border rounded px-3 py-2"
                value={carrierData.nip}
                onChange={(e) => setCarrierData({ ...carrierData, nip: e.target.value })}
                placeholder="123-456-78-90"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
              <input
                type="email"
                className="w-full border rounded px-3 py-2"
                value={carrierData.email}
                onChange={(e) => setCarrierData({ ...carrierData, email: e.target.value })}
                placeholder="kontakt@przewoznik.pl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
              <input
                type="text"
                className="w-full border rounded px-3 py-2"
                value={carrierData.phone}
                onChange={(e) => setCarrierData({ ...carrierData, phone: e.target.value })}
                placeholder="+48 123 456 789"
              />
            </div>
          </div>

          {/* Spedytorzy */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-semibold text-gray-800">Spedytorzy ({forwarders.length})</label>
              <Button size="sm" onClick={handleAddForwarder} className="bg-green-600 hover:bg-green-700 text-white">
                + Dodaj spedytora
              </Button>
            </div>
            {forwarders.length === 0 ? (
              <div className="text-center py-6 text-gray-500 bg-gray-50 rounded">
                Brak spedytorów. Kliknij "Dodaj spedytora" aby dodać.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {forwarders.map((fwd, idx) => (
                  <div key={idx} className="border rounded-lg p-3 bg-blue-50 border-blue-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-semibold text-blue-900">{fwd.name}</div>
                        {fwd.email && <div className="text-xs text-blue-700">{fwd.email}</div>}
                        {fwd.phone && <div className="text-xs text-blue-700">{fwd.phone}</div>}
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleEditForwarder(idx)}
                          className="p-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                          title="Edytuj"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteForwarder(idx)}
                          className="p-1.5 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                          title="Usuń"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Kierowcy */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-semibold text-gray-800">Kierowcy ({drivers.length})</label>
              <Button size="sm" onClick={handleAddDriver} className="bg-green-600 hover:bg-green-700 text-white">
                + Dodaj kierowcę
              </Button>
            </div>
            {drivers.length === 0 ? (
              <div className="text-center py-6 text-gray-500 bg-gray-50 rounded">
                Brak kierowców. Kliknij "Dodaj kierowcę" aby dodać.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {drivers.map((drv, idx) => (
                  <div key={idx} className="border rounded-lg p-3 bg-green-50 border-green-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-semibold text-green-900">{drv.name}</div>
                        {drv.email && <div className="text-xs text-green-700">{drv.email}</div>}
                        {drv.phone && <div className="text-xs text-green-700">{drv.phone}</div>}
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleEditDriver(idx)}
                          className="p-1.5 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                          title="Edytuj"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteDriver(idx)}
                          className="p-1.5 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                          title="Usuń"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={onClose}>Anuluj</Button>
            <Button onClick={handleSaveCarrier} className="bg-blue-600 hover:bg-blue-700 text-white">
              Zapisz zmiany
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal dodawania/edycji spedytora */}
      <Modal
        isOpen={forwarderModal.open}
        onClose={() => setForwarderModal({ open: false, index: null })}
        title={forwarderModal.index !== null ? 'Edytuj spedytora' : 'Dodaj spedytora'}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Imię i nazwisko *</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={forwarderForm.name}
              onChange={(e) => setForwarderForm({ ...forwarderForm, name: e.target.value })}
              placeholder="Jan Kowalski"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
            <input
              type="email"
              className="w-full border rounded px-3 py-2"
              value={forwarderForm.email}
              onChange={(e) => setForwarderForm({ ...forwarderForm, email: e.target.value })}
              placeholder="jan.kowalski@example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={forwarderForm.phone}
              onChange={(e) => setForwarderForm({ ...forwarderForm, phone: e.target.value })}
              placeholder="+48 123 456 789"
            />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setForwarderModal({ open: false, index: null })}>
              Anuluj
            </Button>
            <Button onClick={handleSaveForwarder} className="bg-blue-600 hover:bg-blue-700 text-white">
              {forwarderModal.index !== null ? 'Zapisz' : 'Dodaj'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal dodawania/edycji kierowcy */}
      <Modal
        isOpen={driverModal.open}
        onClose={() => setDriverModal({ open: false, index: null })}
        title={driverModal.index !== null ? 'Edytuj kierowcę' : 'Dodaj kierowcę'}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Imię i nazwisko *</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={driverForm.name}
              onChange={(e) => setDriverForm({ ...driverForm, name: e.target.value })}
              placeholder="Jan Kowalski"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
            <input
              type="email"
              className="w-full border rounded px-3 py-2"
              value={driverForm.email}
              onChange={(e) => setDriverForm({ ...driverForm, email: e.target.value })}
              placeholder="jan.kowalski@example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={driverForm.phone}
              onChange={(e) => setDriverForm({ ...driverForm, phone: e.target.value })}
              placeholder="+48 123 456 789"
            />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDriverModal({ open: false, index: null })}>
              Anuluj
            </Button>
            <Button onClick={handleSaveDriver} className="bg-green-600 hover:bg-green-700 text-white">
              {driverModal.index !== null ? 'Zapisz' : 'Dodaj'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
